import {
    AlertOriginTypesEnum,
    AlertTypes,
    AlertTypesEnum,
    ApplicationHelpType,
    DataScopesEnum,
    EmptyGuid,
    EntityTypes,
    LookupDataEnums,
    NotePurposeTypesEnum,
    NoteStatusTypes,
    NoteTypes,
    ReviewStatusTypesEnum,
    UserRightsEnum,
} from '../../../utilities/Constants';
import {
    IAlertUser,
    INoteCategoryModel,
    INoteCategoryTypeModel,
    INoteConversationModel,
    INoteModel,
    INotesUICustomization,
} from '../../../interfaces/INote';
import {
    IAssertionDescriptorModel,
    IEntityAssertionModel,
} from '../../../interfaces/IAssertionDescriptor';
import { ICaseTitleModel, IDocumentModel } from '../../../interfaces/ICase';
import { IErrorContent, IValidation } from '../../../interfaces/IError';
import { IKeyValuePairModel, ILookupModel, IMultiSelectOption } from '../../../interfaces/ILookup';
import { cloneDeep, isEmpty } from 'lodash';
import { getDocumentSubTypeId, getDocumentTypeId } from './NoteHelper';
import { useEffect, useMemo, useState } from 'react';

import Authorization from '../../../stores/Authorization';
import { CheckmateDialog } from '../../shared/dialog';
import Common from '../../../stores/Common';
import { DocumentsList } from '../../documents-list/DocumentsList';
import { IHelpProps } from '../../shared/Help';
import { IQuadZoneNoteRequest } from '../../../interfaces/IQuad';
import { IUserModel } from '../../../interfaces/IUser';
import { Loader } from '../../shared/Loader';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import NoteAuthHelper from '../../../utilities/NoteAuthHelper';
import { NoteEditorAdditionalFieldsByNoteCategory } from './NoteEditorAdditionalFieldsByNoteCategory';
import { NoteEditorChat } from './NoteEditorChat';
import { NoteEditorFields } from './NoteEditorFields';
import NoteEditorFieldsReadOnly from './NoteEditorFieldsReadOnly';
import NoteEditorHeader from './NoteEditorHeader';
import { NoteEditorHistoryModal } from './NoteEditorHistoryModal';
import NoteHelper from '../../../utilities/NoteHelper';
import { PageWithAffixedHeader } from '../../../shared/PageWithAffixedHeader';
import { RelatedEntitiesModal } from '../RelatedEntitiesModal';
import Sort from '../../../stores/Sort';
import UIHelper from '../../../utilities/UIHelper';
import { UnsavedChangesBlocker } from '../../../shared/UnsavedChangesBlocker';
import ValidateUtils from '../../../shared/validations';
import { WatchListModal } from '../WatchListModal';
import { showErrorToast } from '../../../shared/ToastUtils';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useCreateZoneNoteMutation } from '../../../pages/quad/zone-notes/useCreateZoneNoteMutation';
import { useGetLookupData } from '../../../shared/react-query-hooks/useGetLookupData';
import { useGetNoteTargets } from '../../../shared/react-query-hooks/useGetNoteTargets';
import { useGetQuadZoneNoteTargets } from '../../../pages/quad/zone-notes/useGetQuadZoneNoteTargets';
import { useNavigate } from 'react-router-dom';
import { useZoneContext } from '../../../contexts/useZoneContext';

export interface INoteEditorProps {
    asOverlay?: boolean;
    caseLocalCounselGuid?: string;
    caseTitle?: ICaseTitleModel;
    currentNote: INoteModel;
    defaultAlertTarget?: IUserModel;
    defaultWatchTargets?: IUserModel[];
    enableNoteAssociations?: boolean;
    helpText?: ILookupModel[];
    isQuadNoteEditor?: boolean;
    isReadonlyMode?: boolean;
    noteAssociationEntityItems?: IMultiSelectOption[];
    noteAssociationEntityType?: ILookupModel;
    noteAssociationIcon?: string;
    noteAssociationTitle?: string;
    noteCategoryTypes: INoteCategoryTypeModel[] | undefined;
    notesUICustomization?: INotesUICustomization;
    onCancel: (alertGuid: string) => void;
    onSaveComplete: (note: INoteModel) => void;
    strategyName?: string;
    originEntityKey?: string;
    originEntityName?: string;
    originEntityType?: number;
    originStrategyType?: number;
    strategyTypeId?: number;
    useNoteAssociationsModal?: boolean;
}

const QUAD_HELP_TITLE = 'Quad Zone Note Upload';
const QUAD_HELP_TEXT = 'Document upload not allowed for Quad Zone Notes.';

export function NoteEditor(props: INoteEditorProps) {
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();
    const navigate = useNavigate();

    const createZoneNoteMutation = useCreateZoneNoteMutation();

    const isNewNote = !props.currentNote.guid || props.currentNote.guid === EmptyGuid;

    const getStrategyTypeId = () => {
        return props.strategyTypeId ?? currentNote?.originStrategyType;
    };

    const notePurposeTypes = useGetLookupData(LookupDataEnums.NotePurposeTypes);
    const approvalStatueTypes = useGetLookupData(LookupDataEnums.ApprovalStatusTypes);
    const acknowledgmentStatusTypes = useGetLookupData(LookupDataEnums.AcknowledgmentStatusTypes);
    const [allAlertTargets, setAllAlertTargets] = useState<IMultiSelectOption[]>([]);
    const [allUsers, setAllUsers] = useState<IUserModel[]>([]);
    const [currentNote, setCurrentNote] = useState<INoteModel>({} as INoteModel);
    const [isReadonlyMode, setIsReadonlyMode] = useState<boolean | undefined>(props.isReadonlyMode);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean | undefined>(undefined);
    const [keepDocumentUploadDialogOpen, setKeepDocumentUploadDialogOpen] = useState<
        boolean | undefined
    >(undefined);
    const [noteStateChanged, setNoteStateChanged] = useState<boolean | undefined>(undefined);
    const [originalNote, setOriginalNote] = useState<INoteModel>({} as INoteModel);
    const [pendingChatMessagePost, setPendingChatMessagePost] = useState<boolean | undefined>(
        undefined
    );
    const [loadingNoteData, setLoadingNoteData] = useState<boolean>(true);
    const [pendingResponse, setPendingResponse] = useState<boolean>(true);
    const [returnToPriorTarget, setReturnToPriorTarget] = useState<boolean>(false);
    const [savedNote, setSavedNote] = useState<INoteModel>({} as INoteModel);
    const [selectedAlertTarget, setSelectedAlertTarget] = useState<IMultiSelectOption>(
        {} as IMultiSelectOption
    );
    const [selectedEntityItems, setSelectedEntityItems] = useState<
        IMultiSelectOption[] | undefined
    >(undefined);
    const [selectedNoteCategories, setSelectedNoteCategories] = useState<IMultiSelectOption[]>([]);
    const [selectedOrganizations, setSelectedOrganizations] = useState<IMultiSelectOption[]>([]);
    const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
    const [showNoteAssociationsModal, setShowNoteAssociationsModal] = useState<boolean>(false);
    const [showUnsavedChangesOnCancelDialog, setShowUnsavedChangesOnCancelDialog] =
        useState<boolean>(false);
    const [showWatchGroup, setShowWatchGroup] = useState<boolean | undefined>(undefined);
    const [showNoDecisionModal, setShowNoDecisionModal] = useState<boolean>(false);
    const [showAddtionalQuadZoneNoteModal, setShowAddtionalQuadZoneNoteModal] =
        useState<boolean>(false);
    const [validation, setValidation] = useState<IValidation>({} as IValidation);

    const noteTargets = useGetNoteTargets(
        props.currentNote?.type?.id,
        props.caseLocalCounselGuid,
        getStrategyTypeId(),
        props.isQuadNoteEditor
    );

    const quadNoteTargets = useGetQuadZoneNoteTargets(
        selectedOrganizations.map((o) => o.guid || ''),
        props.isQuadNoteEditor
    );

    const handleChangeValue = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLTextAreaElement>
            | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const name = event.target.name;
        const value = event.target.value;

        const currentNoteCopy = cloneDeep(currentNote);
        if (!currentNoteCopy) return;

        const validationCopy = cloneDeep(validation);
        const selectedAlertTargetCopy = cloneDeep(selectedAlertTarget);

        switch (name) {
            case 'noteContent':
                currentNoteCopy.content = value;
                validationCopy.content = [''];
                break;
            case 'noteContext':
                currentNoteCopy.context = value;
                validationCopy.context = [''];
                break;
            case 'status':
                currentNoteCopy.status = { id: parseInt(value) };
                break;
            case 'approvalStatus':
                currentNoteCopy.approvalStatus = value ? { id: parseInt(value) } : undefined;
                break;
            case 'acknowledgmentStatus':
                currentNoteCopy.acknowledgmentStatus = value ? { id: parseInt(value) } : undefined;
                break;

            default:
                break;
        }

        setCurrentNote(currentNoteCopy);
        setSelectedAlertTarget(selectedAlertTargetCopy);
        setValidation(validationCopy);
        setHasUnsavedChanges(true);
    };

    const handleNoteCategoriesSelected = (optionsList: any) => {
        const updatedNote = NoteHelper.updateCurrentNoteOnCategoryChange(
            currentNote!,
            optionsList,
            props.noteCategoryTypes ?? []
        );

        setCurrentNote(updatedNote);
        setSelectedNoteCategories(optionsList);
        setHasUnsavedChanges(true);
    };

    const handleNoteAssociationSelected = (optionsList: any) => {
        if (!currentNote || !props.noteAssociationEntityType) return;
        const currentNoteCopy = currentNote;
        currentNoteCopy.associations = [];
        if (optionsList) {
            for (let i = 0; i < optionsList.length; i++) {
                currentNoteCopy.associations.push({
                    entityType: props.noteAssociationEntityType,
                    entityGuid: optionsList[i].guid,
                    displayName: optionsList[i].label,
                });
            }
        }

        setCurrentNote(currentNoteCopy);
        setSelectedEntityItems(optionsList);
        setHasUnsavedChanges(true);
    };

    const getAlertOriginTypeBasedOnNoteType = (): ILookupModel => {
        const currentNoteType =
            currentNote !== undefined ? currentNote.type : props.currentNote.type;

        const alertOriginType: ILookupModel = { id: AlertOriginTypesEnum.CaseNote };

        switch (currentNoteType?.id) {
            case NoteTypes.CaseNote:
                return alertOriginType;
            case NoteTypes.ZoneNote: {
                alertOriginType.id = AlertOriginTypesEnum.ZoneNote;
                return alertOriginType;
            }
            case NoteTypes.StrategyNote: {
                alertOriginType.id = AlertOriginTypesEnum.StrategyNote;
                return alertOriginType;
            }
            case NoteTypes.PackageSettlementNote: {
                alertOriginType.id = AlertOriginTypesEnum.PackageSettlementNote;
                return alertOriginType;
            }
            default:
                return alertOriginType;
        }
    };

    const validateNote = (currentNote: INoteModel, noteCategoryTypes: INoteCategoryTypeModel[]) => {
        const validation: IValidation = {};

        const purposeId: any = currentNote.purpose.id;
        if (!currentNote.purpose.id || purposeId == '') {
            validation.notePurpose = ['Required'];
        }

        if (!currentNote.content || currentNote.content!.trim().length == 0) {
            validation.content = ['Required'];
        }

        if (!currentNote.purpose.id) {
            validation.notePurpose = ['Required'];
        } else {
            if (
                (currentNote.purpose.id === NotePurposeTypesEnum.Approval ||
                    currentNote.purpose.id === NotePurposeTypesEnum.Acknowledgement) &&
                (!selectedAlertTarget.guid || selectedAlertTarget.guid === EmptyGuid)
            )
                validation.actionTarget = ['Required'];
        }

        let subCategoryMissing = false;

        if (!currentNote.categories || currentNote.categories!.length === 0) {
            validation.categories = ['At least One Note Category is required'];
        } else if (currentNote.categories) {
            const categoryValidationErrors: string[] = [];

            currentNote.categories.forEach((noteCategoryItem: INoteCategoryModel) => {
                const noteCategoryTypeLookupItem = noteCategoryTypes.find(
                    (x) => x.id == noteCategoryItem.id
                );
                let additionalInfoMissing = false;
                if (noteCategoryTypeLookupItem) {
                    if (
                        noteCategoryTypeLookupItem.assertionDescriptors &&
                        noteCategoryTypeLookupItem.assertionDescriptors.length > 0
                    ) {
                        if (
                            !noteCategoryItem.assertions ||
                            noteCategoryItem.assertions.length == 0
                        ) {
                            additionalInfoMissing = true;
                        } else {
                            noteCategoryTypeLookupItem.assertionDescriptors.map(
                                (assertionDescriptor: IAssertionDescriptorModel) => {
                                    const foundAD = noteCategoryItem.assertions!.find(
                                        (entityAssertion) =>
                                            entityAssertion.assertionDescriptorGuid ==
                                            assertionDescriptor.guid
                                    );
                                    if (
                                        foundAD &&
                                        !foundAD.assertionDescriptorValueGuid &&
                                        !foundAD.dateTimeValue &&
                                        !foundAD.textValue &&
                                        !foundAD.booleanValue &&
                                        !foundAD.numericValue
                                    ) {
                                        additionalInfoMissing = true;
                                    }
                                }
                            );
                        }
                    }

                    if (
                        (noteCategoryTypeLookupItem.subCategories?.length ?? 0) > 0 &&
                        (!noteCategoryItem.subCategory || noteCategoryItem.subCategory.id === 0)
                    ) {
                        subCategoryMissing = true;
                    }

                    if (additionalInfoMissing) {
                        categoryValidationErrors.push(
                            `${noteCategoryItem.id}|Additional Fields required for Note`
                        );
                    }
                }
            });

            if (categoryValidationErrors.length > 0) {
                validation.additionalFields = categoryValidationErrors;
            }

            if (subCategoryMissing) {
                validation.subCategory = ['Sub Category is Required'];
            }
        }

        if (pendingChatMessagePost) {
            validation.model = ['You have a chat message that has not been posted.'];
        }

        if (isActionTargetLocal() && currentNote.private) {
            validation.model = ['Cannot set a Local Action Target for a Private Note.'];
        }

        if (props.isQuadNoteEditor && selectedOrganizations.length < 2) {
            validation.organizationGuids = ['At least Two Zones are required'];
        }

        return validation;
    };

    const saveNote = async (keepDocumentUploadDialogOpen?: boolean, stayInEditMode?: boolean) => {
        if (!currentNote) return;
        const noteValidation = validateNote(currentNote!, props.noteCategoryTypes ?? []);

        if (!isEmpty(noteValidation)) {
            setValidation(noteValidation);
            setPendingResponse(false);
            return;
        }

        const currentNoteCopy = cloneDeep(currentNote);

        if (selectedAlertTarget) {
            const originType = getAlertOriginTypeBasedOnNoteType();

            if (!currentNoteCopy.alert) currentNoteCopy.alert = {};
            if (!currentNoteCopy.alert.users) currentNoteCopy.alert.users = [];

            currentNoteCopy.alert.originType = originType;

            // remove all existing Action Targets
            for (let i = 0; i < currentNoteCopy.alert.users.length; i++) {
                if (
                    currentNoteCopy.alert.users[i].type &&
                    currentNoteCopy.alert.users[i].type!.id === AlertTypes.Act.Value
                )
                    currentNoteCopy.alert.users.splice(i, 1);
            }

            const match = allUsers.find((x) => x.guid === selectedAlertTarget.guid);
            if (match) {
                const alertUserToAdd: IAlertUser = {
                    user: cloneDeep(match),
                    read: false,
                    type: { id: AlertTypes.Act.Value },
                };
                currentNoteCopy.alert.users.push(alertUserToAdd);
            }
        }

        if (props.isQuadNoteEditor) {
            const organizationGuids = selectedOrganizations
                .map((o) => o.guid || '')
                .filter((g) => g);

            handleQuadSave(organizationGuids, currentNoteCopy);
        } else {
            setPendingResponse(true);
            const response = await zoneContext.zoneApiClient.saveNote(currentNoteCopy!);
            if (response.httpResponse.status == 401) {
                window.location.reload();
                return;
            }

            if (!response.httpResponse.ok) {
                setPendingResponse(false);

                const nonCaseNoteTitle =
                    props.currentNote.originEntityName ||
                    props.originEntityName ||
                    props.currentNote.packageDealName;

                const noteTitle = !isCaseNote ? nonCaseNoteTitle : props.caseTitle?.caseName;

                showErrorToast(`Failed to save note - ${noteTitle}`);
                return;
            }

            if (!response.httpResponse.ok) {
                setCurrentNote(cloneDeep(originalNote));
                setHasUnsavedChanges(false);
                setValidation({});
                setReturnToPriorTarget(false);
                return;
            }

            const savedNote = response.payload!;
            const selectedNoteCategories: IMultiSelectOption[] = [];
            let selectedEntityItems: IMultiSelectOption[] = [];

            if (savedNote.categories !== undefined) {
                for (let i = 0; i < savedNote.categories.length; i++) {
                    selectedNoteCategories.push({
                        label: savedNote.categories[i].name!,
                        value: savedNote.categories[i].name!,
                        id: savedNote.categories[i].id,
                    });
                }
            }

            if (props.currentNote.associations !== undefined) {
                selectedEntityItems = getSelectedEntityItems(savedNote);
            }

            setShowNoDecisionModal(false);
            setCurrentNote(savedNote);
            setOriginalNote(cloneDeep(savedNote));
            setSelectedNoteCategories(selectedNoteCategories);
            setSelectedEntityItems(selectedEntityItems);
            setPendingResponse(false);
            setHasUnsavedChanges(false);
            setNoteStateChanged(true);
            setSavedNote(savedNote);
            setValidation({});
            setKeepDocumentUploadDialogOpen(keepDocumentUploadDialogOpen);
            setIsReadonlyMode(!stayInEditMode);
        }
    };

    const handleNoteCategoryAdditionalFieldChange = (items: IEntityAssertionModel[], key?: any) => {
        const item = items[0];
        const noteCategoryId = parseInt(key!);
        const currentNoteCopy = cloneDeep(currentNote);

        if (!currentNoteCopy.categories) return;

        const noteCategoryMatch = currentNoteCopy.categories.find((x) => x.id == noteCategoryId);

        if (noteCategoryMatch) {
            if (!noteCategoryMatch.assertions || noteCategoryMatch.assertions.length === 0) {
                noteCategoryMatch.assertions = [item];
            } else {
                const assertionMatchIndex = noteCategoryMatch.assertions.findIndex(
                    (x) => x.assertionDescriptorGuid == item.assertionDescriptorGuid
                );

                if (assertionMatchIndex > -1) {
                    noteCategoryMatch.assertions.splice(assertionMatchIndex, 1, item);
                } else {
                    noteCategoryMatch.assertions.push(item);
                }
            }

            setCurrentNote(currentNoteCopy);
            setHasUnsavedChanges(true);
        }
    };

    const handleReviewRequired = () => {
        const currentNoteCopy = cloneDeep(currentNote);
        let returnToPriorTargetCopy = cloneDeep(returnToPriorTarget);
        let selectedAlertTargetCopy = cloneDeep(selectedAlertTarget);

        if (currentNote.reviewStatus) {
            const reviewStatusId = currentNote.reviewStatus.id;
            if (currentNoteCopy.reviewStatus != undefined && currentNoteCopy.reviewStatus != null) {
                if (reviewStatusId == ReviewStatusTypesEnum.Reviewed) {
                    currentNoteCopy.reviewStatus.id = ReviewStatusTypesEnum.ReviewRequired;
                } else {
                    //ReviewRequired
                    currentNoteCopy.reviewStatus.id = ReviewStatusTypesEnum.Reviewed;
                }
            }
        }

        setCurrentNote(currentNoteCopy);
        setHasUnsavedChanges(true);
        setSelectedAlertTarget(selectedAlertTargetCopy);
        setReturnToPriorTarget(returnToPriorTargetCopy);
    };

    const handleCheckChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentNoteCopy = cloneDeep(currentNote);
        let returnToPriorTargetCopy = cloneDeep(returnToPriorTarget);
        let selectedAlertTargetCopy = cloneDeep(selectedAlertTarget);

        switch (event.target.name) {
            case 'chkStarred':
                currentNoteCopy.starred = event.target.checked;
                break;
            case 'chkPrivate':
                currentNoteCopy.private = event.target.checked;
                break;
            case 'chkAllowLocalEdit':
                currentNoteCopy.allowLocalEdit = event.target.checked;
                break;
            case 'chkModifiedBeforeApproval':
                currentNoteCopy.modifiedBeforeApproval = event.target.checked;
                break;
            case 'chkReturnToPriorTarget':
                returnToPriorTargetCopy = event.target.checked;
                selectedAlertTargetCopy = { id: 0, label: '', value: '' };

                if (returnToPriorTargetCopy) {
                    const alert = currentNoteCopy.alert;

                    const sortedHistory = [...(alert?.actionTargetHistory ?? [])].sort(
                        (a, b) => (b.sortOrder ?? 0) - (a.sortOrder ?? 0)
                    );

                    const targetUser =
                        sortedHistory.length > 1 ? sortedHistory[1]?.user : alert?.createdBy;

                    if (targetUser) {
                        selectedAlertTargetCopy = {
                            id: 0,
                            label: getUserFullNameDisplay(targetUser!),
                            value: targetUser!.guid!,
                            guid: targetUser!.guid!,
                        };
                    }
                } else {
                    const currentTarget = currentNoteCopy.alert?.users?.find(
                        (x) => x.type!.id === AlertTypes.Act.Value && !x.endDate
                    );

                    if (currentTarget) {
                        selectedAlertTargetCopy = {
                            id: 0,
                            label: getUserFullNameDisplay(currentTarget.user!),
                            value: currentTarget.user!.guid!,
                            guid: currentTarget.user!.guid!,
                        };
                    }
                }
                break;
            default:
        }

        setCurrentNote(currentNoteCopy);
        setHasUnsavedChanges(true);
        setSelectedAlertTarget(selectedAlertTargetCopy);
        setReturnToPriorTarget(returnToPriorTargetCopy);
    };

    const handleSaveDocumentComplete = (documents: IDocumentModel[]) => {
        const currentNoteCopy = cloneDeep(currentNote);
        if (!currentNoteCopy.documents) currentNoteCopy.documents = [];

        for (const document of documents) {
            const currentNoteMatchedDoc = currentNoteCopy.documents?.find(
                (doc) => doc.guid === document.guid
            );
            if (currentNoteMatchedDoc) {
                Object.assign(currentNoteMatchedDoc, document);
            } else {
                currentNoteCopy.documents.push(document);
            }
        }
        currentNoteCopy.hasAttachment = true;

        const savedNoteCopy = cloneDeep(savedNote);
        if (!savedNoteCopy.documents) savedNoteCopy.documents = [];
        for (const document of documents) {
            const savedNoteMatchedDoc = savedNoteCopy.documents?.find(
                (x) => x.guid === document.guid
            );
            if (savedNoteMatchedDoc) {
                Object.assign(savedNoteMatchedDoc, document);
            } else {
                savedNoteCopy.documents.push(document);
            }
        }
        savedNoteCopy.hasAttachment = true;

        setCurrentNote(currentNoteCopy);
        setKeepDocumentUploadDialogOpen(false);
        setNoteStateChanged(true);
        setSavedNote(savedNoteCopy);
    };

    const handleDeleteDocumentComplete = (documentGuid: string) => {
        const currentNoteCopy = cloneDeep(currentNote);
        const savedNoteCopy = cloneDeep(savedNote);

        if (currentNoteCopy.documents) {
            const matchIndex = currentNoteCopy.documents.findIndex((x) => x.guid === documentGuid);
            if (matchIndex >= 0) {
                currentNoteCopy.documents.splice(matchIndex, 1);
                currentNoteCopy.hasAttachment = currentNoteCopy.documents.length > 0;
            }
        }

        if (savedNoteCopy.documents) {
            const matchIndex = savedNoteCopy.documents.findIndex((x) => x.guid === documentGuid);
            if (matchIndex >= 0) {
                savedNoteCopy.documents.splice(matchIndex, 1);
                savedNoteCopy.hasAttachment = savedNoteCopy.documents.length > 0;
            }
        }

        setCurrentNote(currentNoteCopy);
        setNoteStateChanged(true);
        setSavedNote(savedNoteCopy);
    };

    const handleSearchableSingleSelectChange = (
        selectedItem: IMultiSelectOption | undefined,
        identifier: string
    ) => {
        const currentNoteCopy = cloneDeep(currentNote);
        let selectedAlertTargetCopy = cloneDeep(selectedAlertTarget);

        switch (identifier) {
            case 'actionTarget':
                if (selectedItem) selectedAlertTargetCopy = selectedItem;
                else selectedAlertTargetCopy = { id: 0, label: '', value: '' };
                break;
            case 'subCategory':
                if (currentNoteCopy.categories) {
                    const value = selectedItem?.id ?? 0;
                    const category = getCategoryBySubCategoryId(value);

                    if (!category) break;

                    const matchInCurrentNote = currentNoteCopy.categories.find(
                        (x) => x.id === category.id
                    );
                    if (matchInCurrentNote) {
                        matchInCurrentNote.subCategory = value ? { id: value } : undefined;
                    }
                    if (value) {
                        const categoryLookupMatch = props.noteCategoryTypes?.find(
                            (x) => x.id === category.id
                        );
                        if (categoryLookupMatch && categoryLookupMatch.subCategories) {
                            const subCategoryLookupMatch = categoryLookupMatch.subCategories.find(
                                (x) => x.id === value
                            );
                            if (subCategoryLookupMatch) {
                                if (
                                    !currentNoteCopy.content &&
                                    subCategoryLookupMatch.contentTemplateText
                                )
                                    currentNoteCopy.content =
                                        subCategoryLookupMatch.contentTemplateText;
                                if (
                                    !currentNoteCopy.context &&
                                    subCategoryLookupMatch.contextTemplateText
                                )
                                    currentNoteCopy.context =
                                        subCategoryLookupMatch.contextTemplateText;
                            }
                        }
                    }
                }
                break;
            case 'purpose': {
                const purposeId: number = selectedItem?.id ?? 0;
                const newPurpose = notePurposeTypes.data?.find((f) => f.id == purposeId);
                currentNoteCopy.purpose = newPurpose!;
                if (
                    currentNoteCopy.purpose.id === NotePurposeTypesEnum.Approval ||
                    currentNoteCopy.purpose.id === NotePurposeTypesEnum.Acknowledgement
                ) {
                    if (!currentNoteCopy.alert) currentNoteCopy.alert = {};
                    if (!selectedAlertTargetCopy.label && props.defaultAlertTarget) {
                        selectedAlertTargetCopy = {
                            id: 0,
                            label:
                                props.defaultAlertTarget.profile!.firstName +
                                ' ' +
                                props.defaultAlertTarget.profile!.lastName,
                            value: props.defaultAlertTarget.guid!,
                            guid: props.defaultAlertTarget.guid!,
                        };
                    }

                    if (props.defaultWatchTargets && props.defaultWatchTargets.length > 0) {
                        for (let i = 0; i < props.defaultWatchTargets.length; i++) {
                            const item = props.defaultWatchTargets[i];
                            if (!currentNoteCopy.alert.users) currentNoteCopy.alert.users = [];
                            if (
                                currentNoteCopy.alert.users.filter(
                                    (x) =>
                                        x.type &&
                                        x.type.id === AlertTypes.Watch.Value &&
                                        x.user &&
                                        x.user.guid === item.guid
                                ).length === 0
                            ) {
                                const watchTarget: IAlertUser = {
                                    user: JSON.parse(JSON.stringify(item)),
                                    type: { id: AlertTypes.Watch.Value },
                                    read: false,
                                };
                                currentNoteCopy.alert.users.push(watchTarget);
                            }
                        }
                    }
                } else {
                    currentNoteCopy.alert = undefined;
                    selectedAlertTargetCopy = { id: 0, label: '', value: '' };
                }
                break;
            }
            case 'approvalStatus': {
                const approvalStatusId: number = selectedItem?.id ?? 0;
                currentNoteCopy.approvalStatus = { id: approvalStatusId };
                break;
            }
            case 'acknowledgmentStatus': {
                const acknowledgmentStatusId: number = selectedItem?.id ?? 0;
                currentNoteCopy.acknowledgmentStatus = { id: acknowledgmentStatusId };
                break;
            }
            default:
                break;
        }

        setCurrentNote(currentNoteCopy);
        setSelectedAlertTarget(selectedAlertTargetCopy);
        setHasUnsavedChanges(true);
    };

    const getCategoryBySubCategoryId = (subCategoryId: number): INoteCategoryModel | undefined => {
        if (!props.noteCategoryTypes) return;

        return props.noteCategoryTypes.find((categoryType) => {
            return categoryType.subCategories?.some((x) => x.id === subCategoryId);
        });
    };

    const handleWatchListChange = (watchUsers: IMultiSelectOption[]) => {
        const currentNoteCopy = cloneDeep(currentNote);
        const previousWatchUsers = cloneDeep(
            currentNoteCopy.alert?.users?.filter(
                (user) => user.type?.id === AlertTypesEnum.Watch
            ) ?? []
        );

        const prevGuids = previousWatchUsers.map((user) => user.user?.guid);
        const currentGuids = watchUsers.map((user) => user.guid);

        const hasChanges = !isEmpty(UIHelper.difference(prevGuids, currentGuids));

        if (!hasChanges) {
            setShowWatchGroup(false);
            return;
        }

        if (currentNoteCopy.alert) {
            currentNoteCopy.alert.users =
                currentNoteCopy.alert.users?.filter(
                    (user) => user.type?.id !== AlertTypesEnum.Watch
                ) ?? [];

            const watchAlertUsers = watchUsers.map((wu) => {
                const match = allUsers.find((x) => x.guid === wu.guid);

                return {
                    type: { id: AlertTypes.Watch.Value },
                    user: cloneDeep(match),
                    read: false,
                    new: previousWatchUsers
                        .map((pwu) => pwu.user!.guid!)
                        .includes(match?.guid ?? ''),
                };
            });

            currentNoteCopy.alert.users = currentNoteCopy.alert.users.concat(watchAlertUsers);
        }

        setCurrentNote(currentNoteCopy);
        setHasUnsavedChanges(hasChanges);
        setShowWatchGroup(false);
    };

    const handleSaveButtonClick = async () => {
        const targetIsCurrentUser = user.guid === selectedAlertTarget?.guid;

        const isAck = currentNote.purpose.id === NotePurposeTypesEnum.Acknowledgement;
        const isApproval = currentNote.purpose.id === NotePurposeTypesEnum.Approval;

        let hasValidDecision = true;

        if (isAck) {
            hasValidDecision = !!currentNote.acknowledgmentStatus;
        } else if (isApproval) {
            hasValidDecision = !!currentNote.approvalStatus;
        }

        if (targetIsCurrentUser && !hasValidDecision) {
            setShowNoDecisionModal(true);
            return;
        }

        await saveNote();
    };

    const handleCancelButtonClick = () => {
        if (props.isQuadNoteEditor) {
            props.onCancel('');
        } else if (hasUnsavedChanges) {
            setShowUnsavedChangesOnCancelDialog(true);
        } else {
            // since docs are handled async, we need to preserve the latest known state of docs when rolling back changes
            const noteWithCurrentDocs = {
                ...cloneDeep(originalNote),
                documents: [...(currentNote.documents ?? [])],
            };
            setCurrentNote(cloneDeep(originalNote));
            setIsReadonlyMode(true);
            setHasUnsavedChanges(false);
            setValidation({});
        }
    };

    const addConversation = (message: string) => {
        if (!message) return;

        if (currentNote.alert) {
            const currentNoteCopy = cloneDeep(currentNote);

            const conv: INoteConversationModel = {
                guid: EmptyGuid,
                text: message,
                author: cloneDeep(user),
                dateUTC: Common.currentUTCTime(),
            };

            if (!currentNoteCopy.conversations) currentNoteCopy.conversations = [];
            currentNoteCopy.conversations.push(conv);

            setCurrentNote(currentNoteCopy);
            setHasUnsavedChanges(true);
            setPendingChatMessagePost(false);
        }
    };

    const getUserFullNameDisplay = (user: IUserModel) => {
        const fullName = `${user.profile!.firstName} ${user.profile!.lastName}`;
        const isLocalSuffix = getIsLocalBasicUser(user) ? ' (Local)' : '';
        return fullName + isLocalSuffix;
    };

    const getChatHistoryBoxHeight = (): string => {
        const mainSectionDiv: any = document.getElementById('NoteMainSection');
        if (mainSectionDiv && mainSectionDiv.clientHeight) {
            return (parseInt(mainSectionDiv.clientHeight) - 135).toString() + 'px';
        }

        return '577.25px';
    };

    const getActionTargetHistoryData = () => {
        let historyData: IKeyValuePairModel[] = [];
        const actionTargetHistory: IAlertUser[] = currentNote.alert?.actionTargetHistory ?? [];
        const sortOrder = 1;

        if (currentNote.alert?.actionTargetHistory) {
            if (currentNote.alert.actionTargetHistory.some((x) => x.sortOrder)) {
                // Only get items with sort order > 0.
                // Sort Order = 0 contains the user who created the alert in case it needs to be sent back the first time.
                const filteredHistory = actionTargetHistory.filter((x) => x.sortOrder);

                historyData = filteredHistory.map((historyItem, i) => ({
                    key: getUserFullNameDisplay(historyItem.user!),
                    sortOrder: sortOrder,
                    value: actionTargetHistory[i].endDate!,
                }));
            }
        }
        return historyData.sort(Sort.compareDate('sortOrder', '', 'desc'));
    };

    const isActionTargetLocal = () => {
        if (selectedAlertTarget.guid) {
            const userMatch = allUsers.find((x) => x.guid === selectedAlertTarget.guid);
            if (userMatch && getIsLocalBasicUser(userMatch)) return true;
        }

        return false;
    };

    const getIsLocalBasicUser = (user: IUserModel) => {
        return Authorization.isLocalScope(user, zoneContext.zoneGuid);
    };

    const alertUserToOptionMapper = (alertUser: IAlertUser) =>
        ({
            label: getUserFullNameDisplay(alertUser!.user!),
            value: alertUser!.user!.guid,
            guid: alertUser!.user!.guid,
        }) as IMultiSelectOption;

    const isAllowLocalEditSystemSet = () => {
        const match = allUsers.find((x) => x.guid === selectedAlertTarget.guid);

        if (match) {
            const datascopeId = Authorization.getUserDefaultOrganizationDataScope(
                user,
                zoneContext.zoneGuid
            );

            return datascopeId === DataScopesEnum.LocalBasic.Value && !currentNote.allowLocalEdit;
        }

        return false;
    };

    const getSelectedEntityItems = (currentNote: INoteModel) => {
        const selectedNoteAssociations: IMultiSelectOption[] = [];
        if (
            (props.enableNoteAssociations || props.useNoteAssociationsModal) &&
            props.noteAssociationEntityItems &&
            currentNote.associations &&
            currentNote.associations.length > 0
        ) {
            for (let i = 0; i < currentNote.associations.length; i++) {
                const match = props.noteAssociationEntityItems.find(
                    (x) =>
                        x.value.toLowerCase() ===
                        currentNote.associations![i].entityGuid?.toLowerCase()
                );
                if (match) selectedNoteAssociations.push(JSON.parse(JSON.stringify(match)));
            }
        }

        return selectedNoteAssociations;
    };

    const markNoteAsRead = async (id: string) => {
        //We do not need to mark Watch alert Note as read explicitly.
        //service side when user responds it automatically set as read.
        const response = await zoneContext.zoneApiClient.markAlertAsRead(id);
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }

        if (!response.httpResponse.ok) {
            const nonCaseNoteTitle =
                props.currentNote.originEntityName ||
                props.originEntityName ||
                props.currentNote.packageDealName;

            const noteTitle = !isCaseNote ? nonCaseNoteTitle : props.caseTitle?.caseName;

            showErrorToast(`Failed to mark note as read for - ${noteTitle}`);
            setPendingResponse(false);
            return;
        }
    };

    const getSelectedAlertTarget = (actionTargets: IAlertUser[]): IMultiSelectOption => {
        let selectedAlertTarget: IMultiSelectOption = { id: 0, label: '', value: '' };
        if (actionTargets.length === 0) {
            if (props.defaultAlertTarget) {
                selectedAlertTarget = {
                    id: 0,
                    label: `${props.defaultAlertTarget.profile!.firstName} ${
                        props.defaultAlertTarget.profile!.lastName
                    }`,
                    value: props.defaultAlertTarget.guid!,
                    guid: props.defaultAlertTarget.guid!,
                };
            }
        } else {
            const user = actionTargets[0].user;
            if (user)
                selectedAlertTarget = {
                    id: 0,
                    label: getUserFullNameDisplay(user),
                    value: user.guid!,
                    guid: user.guid!,
                };
        }

        return selectedAlertTarget;
    };

    const loadNoteData = async (id: string) => {
        setLoadingNoteData(true);
        const response = await zoneContext.zoneApiClient.getNoteDetails(id);
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            setValidation(ValidateUtils.parseErrors(response.errors, response.errorMessage));
            setLoadingNoteData(false);
            return;
        }

        const currentNote = response.payload!;
        const selectedNoteCategories: IMultiSelectOption[] = [];
        let selectedEntityItems: IMultiSelectOption[] = [];
        const hasValidGuid = currentNote.guid && currentNote.guid !== EmptyGuid;
        const hasValidAlert = currentNote.alert?.guid && currentNote.alert.guid !== EmptyGuid;
        const hasUnreadAlert = currentNote.alert?.users?.some(
            (x) => x.user?.guid === user.guid && !x.read
        );
        const hasAlertUsers = currentNote.alert && currentNote.alert.users;

        if (currentNote.categories != undefined) {
            for (let i = 0; i < currentNote.categories.length; i++) {
                selectedNoteCategories.push({
                    label: currentNote.categories[i].name!,
                    value: currentNote.categories[i].name!,
                    id: currentNote.categories[i].id,
                });
            }
        }

        if (currentNote.associations !== undefined) {
            selectedEntityItems = getSelectedEntityItems(currentNote);
        }

        if (hasValidGuid && hasValidAlert && hasUnreadAlert) {
            markNoteAsRead(currentNote.alert!.guid!).then(() => {
                currentNote.alertReadForLoggedInUser = true;
            });
        }

        let selectedAlertTarget: IMultiSelectOption = { id: 0, label: '', value: '' };

        if (hasValidGuid && hasAlertUsers) {
            const actionTargets = currentNote.alert!.users!.filter(
                (x) => x.type && x.type.id === AlertTypes.Act.Value
            );

            selectedAlertTarget = getSelectedAlertTarget(actionTargets);

            if (props.defaultWatchTargets && props.defaultWatchTargets.length > 0) {
                for (let i = 0; i < props.defaultWatchTargets.length; i++) {
                    const item = props.defaultWatchTargets[i];
                    if (
                        !currentNote.alert!.users!.some(
                            (x) =>
                                x.type?.id === AlertTypes.Watch.Value && x.user?.guid === item.guid
                        )
                    ) {
                        const watchTarget: IAlertUser = {
                            user: cloneDeep(item),
                            type: { id: AlertTypes.Watch.Value },
                            read: false,
                        };
                        currentNote.alert!.users!.push(watchTarget);
                    }
                }
            }
        }

        setLoadingNoteData(false);
        setIsReadonlyMode(props.isReadonlyMode);
        setCurrentNote(currentNote);
        setOriginalNote(cloneDeep(currentNote));
        setSelectedNoteCategories(selectedNoteCategories);
        setSelectedEntityItems(selectedEntityItems);
        setSelectedAlertTarget(selectedAlertTarget);
    };

    const handleUnsavedChangesDialogConfirm = () => {
        setCurrentNote(cloneDeep(originalNote));
        setIsReadonlyMode(true);
        setHasUnsavedChanges(false);
        setShowUnsavedChangesOnCancelDialog(false);
        setReturnToPriorTarget(false);
        setSelectedAlertTarget(getSelectedAlertTarget(currentNote.alert?.users ?? []));
        setValidation({});

        if (isNewNote || props.isQuadNoteEditor) {
            props.onCancel('');
        }
    };

    const handleUnsavedChangedClose = () => {
        setShowUnsavedChangesOnCancelDialog(false);
    };

    const handleNoteOrganizationsSelected = (selectedOrgs: IMultiSelectOption[]) => {
        setSelectedOrganizations(selectedOrgs);
        setHasUnsavedChanges(true);
    };

    const handleQuadSave = (organizationGuids: string[], noteModel: INoteModel) => {
        setPendingResponse(true);
        setValidation({});

        createZoneNoteMutation
            .mutateAsync({
                organizationGuids,
                noteModel: noteModel,
            } as IQuadZoneNoteRequest)
            .then(() => {
                setIsReadonlyMode(true);
                setPendingResponse(false);
                setHasUnsavedChanges(false);
                setNoteStateChanged(true);
                setShowAddtionalQuadZoneNoteModal(true);
            })
            .catch((error: IErrorContent) => {
                showErrorToast(error.message ?? 'Failed to save quad note');
            })
            .finally(() => {
                setPendingResponse(false);
            });
    };

    const navigateToQuadDashboard = () => {
        navigate(LocalRoutes.QuadDashboard);
    };

    const handleResetQuadZoneNoteForm = () => {
        setCurrentNote({
            status: { id: NoteStatusTypes.Open },
            purpose: { id: NotePurposeTypesEnum.Info },
            type: { id: NoteTypes.ZoneNote },
        });
        setIsReadonlyMode(false);
        setSelectedOrganizations([]);
        setSelectedNoteCategories([]);
        setShowAddtionalQuadZoneNoteModal(false);
    };

    const handleOnCreateEntityBeforeUpload = () => {
        saveNote(false, true);
    };

    useEffect(() => {
        const allUsers = props.isQuadNoteEditor
            ? (quadNoteTargets.data ?? [])
            : (noteTargets.data ?? []);

        const allAlertTargets: IMultiSelectOption[] = allUsers.map((user) => ({
            id: 0,
            label: getUserFullNameDisplay(user),
            value: user.guid!,
            guid: user.guid!,
        }));

        allAlertTargets.sort(Sort.compareValues('label'));

        setAllAlertTargets(allAlertTargets);
        setAllUsers(allUsers);
        setPendingResponse(false);
    }, [noteTargets.data, quadNoteTargets.data]);

    useEffect(() => {
        if (isNewNote) {
            const noteCopy = cloneDeep(props.currentNote);
            const selectedEntityItems = getSelectedEntityItems(noteCopy);

            setIsReadonlyMode(props.isReadonlyMode);
            setCurrentNote(props.currentNote);
            setOriginalNote(noteCopy);
            setSelectedEntityItems(selectedEntityItems);
            setLoadingNoteData(false);
        } else {
            loadNoteData(props.currentNote.guid!);
        }
    }, []);

    const isLoading =
        pendingResponse ||
        notePurposeTypes.isLoading ||
        acknowledgmentStatusTypes.isLoading ||
        approvalStatueTypes.isLoading;

    const disableUploadButton =
        (currentNote.categories?.length ?? 0) > 0 && currentNote.content && !props.isQuadNoteEditor
            ? false
            : true;

    const isAuthorizedToCreate = useMemo(() => {
        if (!props.currentNote.guid) {
            return NoteAuthHelper.canUserCreateNote(
                user,
                zoneContext.zoneGuid,
                props.currentNote.type?.id ?? 0,
                props.currentNote.originStrategyType
            );
        }
        return false;
    }, [user, zoneContext.zoneGuid, props.currentNote]);

    const isAuthorizedToEdit = useMemo(() => {
        if (currentNote.guid) {
            return NoteAuthHelper.canUserEditNote(
                {
                    ...currentNote,
                    originStrategyType: currentNote.originStrategyType ?? props.originStrategyType,
                },
                user,
                zoneContext.zoneGuid
            );
        }
        return false;
    }, [currentNote, user, zoneContext.zoneGuid, props.originStrategyType, props.currentNote.guid]);

    const showEditorForm =
        (!props.currentNote.guid && isAuthorizedToCreate) ||
        (props.currentNote.guid && isAuthorizedToEdit);

    const showAddRelatedButton =
        isAuthorizedToEdit &&
        (props.noteAssociationEntityItems?.length ?? 0) > 0 &&
        !isReadonlyMode;

    const showAdditionalNoteFields =
        [NoteTypes.CaseNote, NoteTypes.ZoneNote].includes(currentNote.type?.id ?? 0) &&
        Authorization.userHasRight(UserRightsEnum.NoteSecurity, user, zoneContext.zoneGuid);

    const showHelpButton = props.isQuadNoteEditor;

    const helpProps: IHelpProps = {
        type: ApplicationHelpType.Info,
        title: props.isQuadNoteEditor ? QUAD_HELP_TITLE : 'Information',
        helpText: props.isQuadNoteEditor ? QUAD_HELP_TEXT : 'Help goes here.',
    };

    const documentTypeId = getDocumentTypeId(currentNote);
    const documentSubTypeId = getDocumentSubTypeId(currentNote);

    if (loadingNoteData) {
        return <Loader />;
    }

    const isCaseNote = props.currentNote.type?.id === NoteTypes.CaseNote;

    return (
        <>
            <UnsavedChangesBlocker when={!!hasUnsavedChanges && !isLoading} />
            {isLoading && <Loader />}
            <PageWithAffixedHeader
                headerComponent={
                    <>
                        <NoteEditorHeader
                            caseTitle={props.caseTitle ?? {}}
                            currentNote={currentNote}
                            hasUnsavedChanges={hasUnsavedChanges}
                            isAllowLocalEditSystemSet={isAllowLocalEditSystemSet()}
                            isCaseNote={isCaseNote}
                            isPrivate={currentNote.private ?? false}
                            isStarred={currentNote.starred ?? false}
                            isPrivateCheckboxDisabled={
                                isReadonlyMode ||
                                (!currentNote.private &&
                                    (currentNote.allowLocalEdit ||
                                        isAllowLocalEditSystemSet() ||
                                        isActionTargetLocal()))
                            }
                            noteAssociationIcon={props.noteAssociationIcon}
                            noteAssociationTitle={props.noteAssociationTitle}
                            onAddRelatedButtonClick={() => setShowNoteAssociationsModal(true)}
                            onBackButtonClick={() => {
                                if (props.isQuadNoteEditor) {
                                    props.onCancel('');
                                } else if (hasUnsavedChanges) {
                                    setShowUnsavedChangesOnCancelDialog(true);
                                } else {
                                    if (noteStateChanged) {
                                        props.onSaveComplete(savedNote);
                                    } else {
                                        props.onCancel(currentNote.alert?.guid ?? '');
                                    }
                                }
                            }}
                            onCancelButtonClick={handleCancelButtonClick}
                            onEditButtonClick={(): void => {
                                setIsReadonlyMode(false);
                            }}
                            onHistoryButtonClick={() => setShowHistoryModal(true)}
                            onIsStarredCheckChanged={handleCheckChanged}
                            onLocalEditCheckChanged={handleCheckChanged}
                            onPrivateCheckChanged={handleCheckChanged}
                            onSaveButtonClick={handleSaveButtonClick}
                            onWatchListButtonClick={() => setShowWatchGroup(true)}
                            strategyName={props.strategyName ?? ''}
                            readonly={isReadonlyMode}
                            selectedEntityItems={selectedEntityItems ?? []}
                            showAdditionalNoteFields={showAdditionalNoteFields}
                            showAddRelatedButton={showAddRelatedButton}
                            showEditButton={isAuthorizedToEdit && isReadonlyMode}
                            showHistoryButton
                            showWatchListButton={!!currentNote.alert}
                            onIsReviewRequiredChanged={handleReviewRequired}
                        />
                        <ValidationErrors validation={validation} />
                    </>
                }
            >
                <div className="mb-4">
                    {showNoteAssociationsModal && (
                        <RelatedEntitiesModal
                            availableOptions={props.noteAssociationEntityItems ?? []}
                            title={props.noteAssociationTitle ?? ''}
                            validation={validation}
                            onCancel={() => setShowNoteAssociationsModal(false)}
                            onHide={() => setShowNoteAssociationsModal(false)}
                            onSubmit={(selectedItems: IMultiSelectOption[]) => {
                                handleNoteAssociationSelected(selectedItems);
                                setShowNoteAssociationsModal(false);
                            }}
                            selectedEntityItems={selectedEntityItems ?? []}
                        />
                    )}

                    {isReadonlyMode || !showEditorForm ? (
                        <>
                            <div className="row">
                                <NoteEditorFieldsReadOnly
                                    currentNote={currentNote}
                                    getAdditionalFieldsByNoteCategory={(noteCategory) => (
                                        <NoteEditorAdditionalFieldsByNoteCategory
                                            noteCategory={noteCategory}
                                            noteCategoryTypes={props.noteCategoryTypes ?? []}
                                            notesUICustomization={props.notesUICustomization}
                                            onNoteCategoryAdditionalFieldChange={
                                                handleNoteCategoryAdditionalFieldChange
                                            }
                                            forceNoteEditorInReadOnlyMode={true}
                                        />
                                    )}
                                    getUserFullNameDisplay={getUserFullNameDisplay}
                                />
                                <div className="col-sm-3">
                                    <NoteEditorChat
                                        currentNote={currentNote}
                                        onAddMessage={addConversation}
                                        outerChatBoxHeight={getChatHistoryBoxHeight()}
                                        readonly
                                    />
                                </div>
                            </div>

                            <div className="row" style={{ marginTop: 20 }}>
                                <DocumentsList
                                    authorizedToEdit={!isAuthorizedToEdit && !isReadonlyMode}
                                    documents={currentNote.documents}
                                    documentTypeId={documentTypeId}
                                    documentSubTypeId={documentSubTypeId}
                                    disableUploadButton={disableUploadButton}
                                    entityGuid={currentNote.guid!}
                                    entityTypeId={EntityTypes.Note}
                                    keepDocumentUploadDialogOpen={keepDocumentUploadDialogOpen}
                                    noteTypeId={
                                        props.currentNote.type
                                            ? props.currentNote.type.id
                                            : undefined
                                    }
                                    onCreateEntityBeforeUpload={handleOnCreateEntityBeforeUpload}
                                    onSaveComplete={handleSaveDocumentComplete}
                                    onDeleteComplete={handleDeleteDocumentComplete}
                                    parentEntityJson={JSON.stringify(currentNote)}
                                    strategyTypeId={getStrategyTypeId()}
                                    title="Attachments"
                                    uploadOnly={true}
                                    user={user}
                                />
                            </div>
                        </>
                    ) : (
                        <div>
                            <div className="row">
                                <NoteEditorFields
                                    acknowledgmentStatusTypes={acknowledgmentStatusTypes.data ?? []}
                                    allAlertTargets={allAlertTargets}
                                    approvalStatusTypes={approvalStatueTypes.data ?? []}
                                    currentNote={currentNote}
                                    getAdditionalFieldsByNoteCategory={(noteCategory) => (
                                        <NoteEditorAdditionalFieldsByNoteCategory
                                            noteCategory={noteCategory}
                                            noteCategoryTypes={props.noteCategoryTypes ?? []}
                                            notesUICustomization={props.notesUICustomization}
                                            onNoteCategoryAdditionalFieldChange={
                                                handleNoteCategoryAdditionalFieldChange
                                            }
                                            forceNoteEditorInReadOnlyMode={isReadonlyMode ?? false}
                                        />
                                    )}
                                    getUserFullNameDisplay={getUserFullNameDisplay}
                                    isQuadNoteEditor={props.isQuadNoteEditor}
                                    onChangeValue={handleChangeValue}
                                    onCheckChanged={handleCheckChanged}
                                    onNoteAssociationSelected={handleNoteAssociationSelected}
                                    onNoteCategoriesSelected={handleNoteCategoriesSelected}
                                    onNoteCategoryAdditionalFieldChange={
                                        handleNoteCategoryAdditionalFieldChange
                                    }
                                    onNoteOrganizationsSelected={handleNoteOrganizationsSelected}
                                    onSearchableSingleSelectChange={
                                        handleSearchableSingleSelectChange
                                    }
                                    noteCategoryTypes={props.noteCategoryTypes ?? []}
                                    notePurposeTypes={notePurposeTypes.data ?? []}
                                    returnToPriorTarget={returnToPriorTarget}
                                    selectedAlertTarget={selectedAlertTarget}
                                    selectedNoteCategories={selectedNoteCategories}
                                    selectedOrganizations={selectedOrganizations}
                                    validation={validation}
                                />
                                <NoteEditorChat
                                    currentNote={currentNote}
                                    onAddMessage={addConversation}
                                />
                            </div>

                            <div className="row" style={{ marginTop: 20 }}>
                                <DocumentsList
                                    authorizedToEdit
                                    documents={currentNote.documents}
                                    documentTypeId={documentTypeId}
                                    documentSubTypeId={documentSubTypeId}
                                    disableUploadButton={disableUploadButton}
                                    entityGuid={currentNote.guid!}
                                    entityTypeId={EntityTypes.Note}
                                    helpProps={helpProps}
                                    keepDocumentUploadDialogOpen={keepDocumentUploadDialogOpen}
                                    onCreateEntityBeforeUpload={handleOnCreateEntityBeforeUpload}
                                    onDeleteComplete={handleDeleteDocumentComplete}
                                    onSaveComplete={handleSaveDocumentComplete}
                                    noteTypeId={
                                        props.currentNote.type
                                            ? props.currentNote.type.id
                                            : undefined
                                    }
                                    parentEntityJson={JSON.stringify(currentNote)}
                                    showHelpButton={showHelpButton}
                                    strategyTypeId={getStrategyTypeId()}
                                    title="Attachments"
                                    uploadOnly={true}
                                    user={user}
                                />
                            </div>
                        </div>
                    )}

                    {showWatchGroup && (
                        <WatchListModal
                            open={showWatchGroup}
                            availableUsers={allAlertTargets.filter(
                                (user) => user.guid !== selectedAlertTarget.guid
                            )}
                            defaultSelectedUsers={
                                currentNote?.alert?.users
                                    ?.filter((user) => user.type?.id === AlertTypesEnum.Watch)
                                    .map(alertUserToOptionMapper) ?? []
                            }
                            onClose={() => setShowWatchGroup(false)}
                            onSubmit={handleWatchListChange}
                            readonly={isReadonlyMode}
                        />
                    )}

                    <NoteEditorHistoryModal
                        open={showHistoryModal}
                        actionTargetHistory={getActionTargetHistoryData()}
                        decisionHistory={currentNote.decisionHistory}
                        onClose={() => setShowHistoryModal(false)}
                    />

                    <CheckmateDialog
                        isShowingModal={showUnsavedChangesOnCancelDialog}
                        title={'Unsaved Changes Detected'}
                        body={'You have unsaved changes. Are you sure you want to proceed?'}
                        handleClose={handleUnsavedChangedClose}
                        handleConfirm={handleUnsavedChangesDialogConfirm}
                        cancelText={'Cancel'}
                        confirmText={'Continue'}
                        closeButtonClassName="btn btn-default"
                        confirmButtonClassName="btn btn-black"
                        size="sm"
                        showBackdrop
                    />

                    <CheckmateDialog
                        isShowingModal={showNoDecisionModal}
                        title={'Decision Not Set'}
                        body={
                            'You are the action target. You are saving this note but have not made a decision or updated the action target to another user. Do you still want to save?'
                        }
                        handleClose={() => setShowNoDecisionModal(false)}
                        handleConfirm={() => saveNote()}
                        cancelText={'Cancel'}
                        confirmText={'Save'}
                        closeButtonClassName="btn btn-default"
                        confirmButtonClassName="btn btn-orange"
                        size="sm"
                    />

                    <CheckmateDialog
                        isShowingModal={showAddtionalQuadZoneNoteModal}
                        title={'Note Successfully Added.'}
                        body={'Do you want to create another note?'}
                        handleClose={() => navigateToQuadDashboard()}
                        handleConfirm={() => handleResetQuadZoneNoteForm()}
                        cancelText={'No'}
                        confirmText={'Yes'}
                        closeButtonClassName="btn btn-default"
                        confirmButtonClassName="btn btn-orange"
                        size="sm"
                        showBackdrop
                    />
                </div>
            </PageWithAffixedHeader>
        </>
    );
}

function ValidationErrors(props: { validation: IValidation }) {
    return (
        <>
            {props.validation.model && (
                <div style={{ padding: '10px 0' }}>
                    <span className="text-danger">{props.validation.model}</span>
                </div>
            )}
        </>
    );
}
